import { sileo } from "sileo";

const defaultErrorTitle = "Algo salió mal";

const TOAST_THEME = {
    success: {
        fill: "#047857",
    },
    info: {
        fill: "#d97706",
    },
    warning: {
        fill: "#b45309",
    },
    error: {
        fill: "#b91c1c",
    },
} as const;

const BASE_TOAST_STYLES = {
    title: "biosam-toast-title",
    description: "biosam-toast-description",
    badge: "biosam-toast-badge",
    button: "biosam-toast-button",
} as const;

type ToastAudience = "admin" | "technician" | "client" | "general";

const ROLE_TO_AUDIENCE: Record<string, ToastAudience> = {
    admin: "admin",
    manager: "admin",
    technician: "technician",
    client: "client",
};

const DEFAULT_LEVEL_DESCRIPTION: Record<
    ToastAudience,
    { success: string; info: string; warning: string; error: string }
> = {
    admin: {
        success: "Operación registrada correctamente en el sistema.",
        info: "Revisa el panel para más detalles.",
        warning: "Conviene revisar este punto para evitar incidencias.",
        error: "No se pudo completar la operación en el sistema.",
    },
    technician: {
        success: "Acción completada. Puedes continuar con la siguiente tarea.",
        info: "Consulta el detalle antes de continuar la intervención.",
        warning: "Atención: revisa este paso antes de seguir en campo.",
        error: "No se pudo completar la acción en campo.",
    },
    client: {
        success: "Todo salió bien. Te mantendremos al tanto.",
        info: "Aquí tienes una actualización sobre tu servicio.",
        warning: "Hay un punto a revisar. Te avisaremos si cambia algo.",
        error: "No pudimos completar la operación en este momento.",
    },
    general: {
        success: "Operación completada correctamente.",
        info: "Hay información nueva disponible.",
        warning: "Revisa este punto antes de continuar.",
        error: "Ha ocurrido un problema durante la operación.",
    },
};

let currentAudience: ToastAudience = "general";

export const setToastAudienceByRole = (role?: string | null): void => {
    if (!role) {
        currentAudience = "general";
        return;
    }

    currentAudience = ROLE_TO_AUDIENCE[role.toLowerCase()] ?? "general";
};

const withDefaultDescription = (
    level: "success" | "info" | "warning" | "error",
    description?: string,
): string => {
    return description?.trim() || DEFAULT_LEVEL_DESCRIPTION[currentAudience][level];
};

const toErrorMessage = (error: unknown): string => {
    if (error instanceof Error && error.message.trim()) {
        return error.message;
    }

    if (typeof error === "string" && error.trim()) {
        return error;
    }

    return withDefaultDescription("error");
};

export const notifySuccess = (title: string, description?: string): string => {
    return sileo.success({
        title,
        description: withDefaultDescription("success", description),
        fill: TOAST_THEME.success.fill,
        styles: BASE_TOAST_STYLES,
    });
};

export const notifyInfo = (title: string, description?: string): string => {
    return sileo.info({
        title,
        description: withDefaultDescription("info", description),
        fill: TOAST_THEME.info.fill,
        styles: BASE_TOAST_STYLES,
    });
};

export const notifyWarning = (title: string, description?: string): string => {
    return sileo.warning({
        title,
        description: withDefaultDescription("warning", description),
        fill: TOAST_THEME.warning.fill,
        styles: BASE_TOAST_STYLES,
    });
};

export const notifyError = (
    error: unknown,
    title: string = defaultErrorTitle,
): string => {
    return sileo.error({
        title,
        description: toErrorMessage(error),
        fill: TOAST_THEME.error.fill,
        styles: BASE_TOAST_STYLES,
    });
};

export const notifyAction = (options: {
    title: string;
    description?: string;
    buttonTitle?: string;
    onClick: () => void;
}): string => {
    return sileo.action({
        title: options.title,
        description: withDefaultDescription("warning", options.description),
        fill: TOAST_THEME.warning.fill,
        styles: BASE_TOAST_STYLES,
        button: {
            title: options.buttonTitle ?? "Confirmar",
            onClick: options.onClick,
        },
        duration: null,
    });
};

export const notifyPromise = sileo.promise;
